import { Label, makeStyles, Spinner, Text } from "@fluentui/react-components";
import { useTagsGet } from "../../hooks/useTags";
import { FlatVsaaTag, VsaaTag } from "../../models/VsaaTag";
import { TreeTagPickerDialog } from "./TreeTagPickerDialog";
import { useMemo, useState } from "react";
import { getTag } from "../../services/TagService";
import { useTranslation } from "react-i18next";
import { VsaaTagGroup } from "./VsaaTagGroup";
import { SkanskaColorTokens } from "../../style/colors";

const useStyles = makeStyles({
  pickerContainer: {
    display: "flex",
    flexDirection: "column",
  },
  labelRow: {
    fontWeight: 600,
    marginBottom: "4px",
  },
  contentRow: {
    display: "flex",
    gap: "8px",
    borderBottom: "2px solid #ccc",
    paddingBottom: "8px",
  },
  errorText: {
    color: SkanskaColorTokens.color_action_red,
    fontSize: "12px",
    marginTop: "4px",
  },
  required: {
    color: "red",
  },
});

interface VsaaTagPickerProps {
  tagCollectionId: string;
  label: string;
  multiValue?: boolean;
  value?: VsaaTag[] | undefined;
  hideDisplay?: boolean;
  isRequired?: boolean;
  onChange: (selectedTags: FlatVsaaTag[]) => void;
  onClose?: () => void;
  usedTagsId?: string[];
  usedTagLabel?: string;
  showPath?: boolean;
  error?: string | null;
  disabled?: boolean;
  processWithSubActivitiesId?: string[];
  disableRootNodes?: boolean;
}

export const VsaaTagPicker = (props: VsaaTagPickerProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [subActivitiesError, setSubActivitiesError] = useState<string | null>(
    null
  );

  const {
    tagCollectionId,
    label,
    multiValue,
    value,
    onChange,
    hideDisplay,
    isRequired,
    onClose,
    usedTagsId,
    usedTagLabel,
    showPath,
    error,
    disabled,
    processWithSubActivitiesId,
    disableRootNodes,
  } = props;

  const {
    data: tags,
    isLoading: isLoadingTags,
    error: getTagsError,
  } = useTagsGet(tagCollectionId);

  const flatValue = useMemo<FlatVsaaTag[]>(() => {
    if (!value || tags === undefined || tags.length === 0) {
      return [];
    }

    if (multiValue) {
      const selectedTagIds = new Set(
        value.map((tag) => getTag(tag.id, tags).id)
      );
      return tags.filter((tag) => selectedTagIds.has(tag.id));
    } else {
      const firstValue = value[0];
      if (!firstValue) return [];
      const defaultSingle = getTag(firstValue.id, tags);
      return defaultSingle ? [defaultSingle] : [];
    }
  }, [value, tags, multiValue]);

  if (getTagsError) {
    throw getTagsError;
  }

  if (isLoadingTags || tags === undefined) {
    return <Spinner>{t("loadingTags")}</Spinner>;
  }

  return (
    <div className={classes.pickerContainer}>
      {!hideDisplay && (
        <Label className={classes.labelRow}>
          {label} {isRequired && <span className={classes.required}>*</span>}
        </Label>
      )}

      <div className={classes.contentRow}>
        <div>
          <TreeTagPickerDialog
            allTags={tags}
            values={flatValue}
            multiValue={multiValue}
            tagCollectionId={tagCollectionId}
            onSubmit={onChange}
            label={label}
            showTagsAsDialogTrigger={hideDisplay}
            onClose={onClose}
            usedTagsId={usedTagsId}
            usedTagLabel={usedTagLabel}
            processWithSubActivitiesId={processWithSubActivitiesId}
            error={error}
            disabled={disabled}
            disableRootNodes={disableRootNodes}
          />
        </div>
        {!hideDisplay && (
          <VsaaTagGroup
            tags={flatValue}
            showPath={showPath}
            onDismiss={(tag) => {
              if (processWithSubActivitiesId?.find((f) => f === tag.id)) {
                setSubActivitiesError(t("processHasSubActivities"));
                return;
              }
              setSubActivitiesError(null);
              const updatedTags = flatValue.filter((t) => t.id !== tag.id);
              onChange(updatedTags);
            }}
          />
        )}
      </div>
      {error && <Text className={classes.errorText}>{error}</Text>}
      {subActivitiesError && (
        <Text className={classes.errorText}>{subActivitiesError}</Text>
      )}
    </div>
  );
};
